import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

import { Subject } from 'rxjs';

import { MtnaPopoverConfig } from '@mtna/lib-ui';
import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';

import {
  RdsVariableSelectionDialogComponent,
  RdsVariableSelectionDialogData,
} from '../variable-selection-dialog/variable-selection-dialog.component';
import { ALL_MEASURE_OPTION_TYPES, MEASURE_OPTIONS, MeasureOptionType } from './measure-option.model';
import { MeasureSelection } from './measure-selection.model';

const DEFAULT_MEASURES: MeasureSelection<any>[] = [new MeasureSelection<any>('COUNT')];

/** Error when invalid control is dirty, touched, or submitted. */
export class RdsMeasureSelectErrorStateMatcher implements ErrorStateMatcher {
  constructor(private _control: NgControl) {}
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(this._control && this._control.invalid && (this._control.dirty || this._control.touched || isSubmitted));
  }
}

/**
 * Select control to select a measure, supports reactive forms.
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-measure-select',
  templateUrl: './measure-select.component.html',
  styleUrls: ['./measure-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RdsMeasureSelectComponent<V extends Variable | VariableSummary = Variable>
  implements ControlValueAccessor, DoCheck, OnDestroy {
  /** Popover configuration to describe the control */
  @Input() config: MtnaPopoverConfig = new MtnaPopoverConfig(
    'Measure',
    `Count/Percentage of records, or statistical aggregation (e.g. sum, average) of numeric variables,
    rolled up by dimensions, that define the content of the table cell values.`,
    'after'
  );

  /** Whether the control is disabled */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);
    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  /**
   * Whether multiple measures can be selected
   * TODO - implement multi selection
   */
  @Input() multiple = false;
  /** Whether the control is required */
  @Input() required = false;
  /** Label for mat-select, defaults to "Measure" */
  @Input() selectLabel = 'Measure';
  /** Placeholder for mat-select, defaults to "Choose measure..." */
  @Input() selectPlaceholder = 'Choose measure...';
  /** Suffix icon for the select, defaults to null */
  @Input() suffixIcon: string | null = null;
  /** Measure types to select from, defaults to all */
  @Input() types: Array<MeasureOptionType> = [...ALL_MEASURE_OPTION_TYPES];

  /** The selected MeasureSelection and value for this control, if it exists */
  @Input()
  set value(value: MeasureSelection<V>[]) {
    if (value !== this.value) {
      this._value = value;
      this._changeDetectorRef.markForCheck();
    }
  }
  get value(): MeasureSelection<V>[] {
    return this._value;
  }
  private _value: MeasureSelection<V>[] = DEFAULT_MEASURES;

  /** Variables to choose from if a measure requires it. */
  @Input() variables: Array<V>;

  /** Changed value of the form control */
  @Output() readonly selectionChange = new EventEmitter<MeasureSelection<V>[]>();

  /** Internal <mat-select> */
  @ViewChild(MatSelect) matSelect: MatSelect;

  /** Custom error state matcher */
  _matcher: RdsMeasureSelectErrorStateMatcher;
  _measureOptions = MEASURE_OPTIONS;

  private _destroyed$ = new Subject<void>();

  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  constructor(
    @Self() @Optional() public _control: NgControl,
    elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _focusMonitor: FocusMonitor
  ) {
    if (this._control) {
      this._control.valueAccessor = this;
      this._matcher = new RdsMeasureSelectErrorStateMatcher(this._control);
    }
    this._focusMonitor.monitor(elementRef, true).subscribe(focusOrigin => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  ngDoCheck(): void {
    if (!!this.matSelect) {
      this.matSelect.updateErrorState();
    }
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this._controlValueAccessorChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  _emitChangeEvent(newValue: MeasureSelection<V>[]) {
    // Measure can never be null
    this.value = newValue !== null && newValue !== undefined ? newValue : DEFAULT_MEASURES;
    this._controlValueAccessorChangeFn(this.value);
    this.selectionChange.emit(this.value);
  }

  /**
   * Manually handle when options are selected/unselected.
   * @param event option selection event
   */
  _handleOptionSelectionChange(type: MeasureOptionType) {
    if (this._measureOptions[type].requiresVariable) {
      // If the user is required to select a variable for the measure, open the dialog
      const ref = this._dialog.open(RdsVariableSelectionDialogComponent, {
        data: new RdsVariableSelectionDialogData<V>(this.variables),
        disableClose: true,
        width: '700px'
      });
      ref.afterClosed().subscribe((output: V) => {
        if (!!output) {
          if (!this.value || !this.value.some(m => m.measureType === type)) {
            // If a new type is being added
            const oldValues = !!this.value ? this.value : [];
            const newValues: MeasureSelection<V>[] = this.multiple
              ? [...oldValues, new MeasureSelection<V>(type, output)]
              : [new MeasureSelection<V>(type, output)];

            this._emitChangeEvent(newValues);
            this._changeDetectorRef.markForCheck();
          }
          const measureWithSameType = !!this.value ? this.value.find(m => m.measureType === type) : null;
          if (!!measureWithSameType && measureWithSameType.variable.id !== output.id) {
            // If the type already existed but the variable changed
            this._emitChangeEvent(this.value.map(m => (m.measureType === type ? new MeasureSelection(type, output) : m)));
            this._changeDetectorRef.markForCheck();
          }
        } else {
          // Reset control if the dialog is cancelled;
          this._emitChangeEvent(DEFAULT_MEASURES);
        }
      });
    } else if (!this.value || !this.value.some(m => m.measureType === type)) {
      // If the option was selected and not already selected, set it
      const oldValues = !!this.value ? this.value : [];
      const newValues: MeasureSelection<V>[] = this.multiple
        ? [...oldValues, new MeasureSelection<V>(type)]
        : [new MeasureSelection<V>(type)];
      this._emitChangeEvent(newValues);
    }
  }

  _unselect(event: MatOptionSelectionChange) {
    if (event.isUserInput) {
      this._emitChangeEvent(DEFAULT_MEASURES);
    }
  }
}
